import { CardDef, CardInstance, CardTier, CardCategory } from '../types';
import { ALL_CARDS, getCardsByTier } from '../cards';

// 簡易UUID生成
function generateId(): string {
  return Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
}

// 新カテゴリ
const NEW_CATEGORIES: CardCategory[] = ['castle', 'city', 'space', 'movie', 'ship', 'ghost', 'park'];

// カードプール管理クラス
export class CardPool {
  private tierPools: Map<CardTier, CardDef[]> = new Map();
  private currentRound: number = 1;

  constructor() {
    this.initializePools();
  }

  // 山札初期化(stockCountに基づいて枚数分追加)
  private initializePools(): void {
    for (const tier of ['A', 'B', 'C'] as CardTier[]) {
      const cards: CardDef[] = [];
      const tierCards = getCardsByTier(tier);
      
      for (const card of tierCards) {
        const count = card.stockCount ?? 4;
        for (let i = 0; i < count; i++) {
          cards.push(card);
        }
      }
      
      this.tierPools.set(tier, this.shuffleArray(cards));
    }
  }

  // ラウンド更新
  updateRound(round: number): CardDef[] {
    this.currentRound = round;
    return [];
  }

  // Tier別にカード取得(山札から引く)
  drawCardsFromTier(tier: CardTier, count: number): CardDef[] {
    const pool = this.tierPools.get(tier) ?? [];
    const drawn = pool.splice(0, Math.min(count, pool.length));
    return drawn;
  }

  // Tier別のカード一覧(参照用)
  getCardsByTier(tier: CardTier): CardDef[] {
    return getCardsByTier(tier);
  }

  // 現在の山札残り枚数
  getTierRemainingCount(tier: CardTier): number {
    return this.tierPools.get(tier)?.length ?? 0;
  }

  // CardDefからCardInstanceを生成
  createInstance(def: CardDef): CardInstance {
    return {
      defId: def.id,
      instanceId: generateId(),
      currentPower: def.basePower,
      isRevealed: false,
    };
  }

  // 複数インスタンス生成
  createInstances(defs: CardDef[]): CardInstance[] {
    return defs.map((d) => this.createInstance(d));
  }

  // ============================================
  // 初期デッキ生成 (ラウンド1用)
  // 6枚、全てTier A
  // ============================================
  generateStarterDeck(): CardInstance[] {
    const tierACards = getCardsByTier('A');
    const starterDefs: CardDef[] = [];
    
    // 各カテゴリーから1枚ずつランダムに選択(重複なし)
    const usedIds = new Set<string>();
    
    for (const cat of NEW_CATEGORIES) {
      const catCards = tierACards.filter((c) => c.category === cat && !usedIds.has(c.id));
      if (catCards.length > 0) {
        const pick = catCards[Math.floor(Math.random() * catCards.length)];
        starterDefs.push(pick);
        usedIds.add(pick.id);
        if (starterDefs.length >= 6) break;
      }
    }

    // 足りない場合はランダム追加
    const remaining = tierACards.filter((c) => !usedIds.has(c.id));
    while (starterDefs.length < 6 && remaining.length > 0) {
      const idx = Math.floor(Math.random() * remaining.length);
      starterDefs.push(remaining.splice(idx, 1)[0]);
    }

    return this.createInstances(starterDefs);
  }

  // ============================================
  // スターターデッキ選択肢を生成
  // ============================================
  generateStarterDeckChoices(): { name: string; description: string; deck: CardInstance[] }[] {
    const tierACards = getCardsByTier('A');

    // テーマ1: バランス型 (各カテゴリから1枚ずつ)
    const balancedDeck = this.createBalancedDeck(tierACards);

    // テーマ2: 攻撃型 (城/映画中心)
    const attackDeck = this.createThemedDeck(tierACards, ['castle', 'movie'], 'castle');

    // テーマ3: トリッキー型 (幽霊/遊園地中心)
    const trickyDeck = this.createThemedDeck(tierACards, ['ghost', 'park'], 'ghost');

    return [
      {
        name: 'バランス型',
        description: '全カテゴリを均等に。安定した立ち回り向け',
        deck: this.createInstances(balancedDeck),
      },
      {
        name: '攻撃型',
        description: '城と映画中心。パワー重視のプレイ向け',
        deck: this.createInstances(attackDeck),
      },
      {
        name: 'トリッキー型',
        description: '幽霊と遊園地中心。除外と山札操作',
        deck: this.createInstances(trickyDeck),
      },
    ];
  }

  // バランス型デッキ生成
  private createBalancedDeck(tierACards: CardDef[]): CardDef[] {
    const deck: CardDef[] = [];
    const usedIds = new Set<string>();

    for (const cat of NEW_CATEGORIES) {
      const catCards = tierACards.filter((c) => c.category === cat && !usedIds.has(c.id));
      if (catCards.length > 0 && deck.length < 6) {
        const pick = catCards[Math.floor(Math.random() * catCards.length)];
        deck.push(pick);
        usedIds.add(pick.id);
      }
    }

    return deck;
  }

  // テーマ型デッキ生成
  private createThemedDeck(
    tierACards: CardDef[],
    mainCategories: CardCategory[],
    primaryCategory: CardCategory
  ): CardDef[] {
    const deck: CardDef[] = [];
    const usedIds = new Set<string>();

    // メインカテゴリーから4枚
    const mainCards = tierACards.filter((c) => mainCategories.includes(c.category));
    const shuffledMain = this.shuffleArray(mainCards);
    
    for (const card of shuffledMain) {
      if (!usedIds.has(card.id) && deck.length < 4) {
        deck.push(card);
        usedIds.add(card.id);
      }
    }

    // 残り2枚は他カテゴリから
    const otherCards = tierACards.filter((c) => !mainCategories.includes(c.category) && !usedIds.has(c.id));
    const shuffledOther = this.shuffleArray(otherCards);
    
    for (const card of shuffledOther) {
      if (deck.length < 6) {
        deck.push(card);
        usedIds.add(card.id);
      }
    }

    return deck;
  }

  // 配列シャッフル
  private shuffleArray<T>(arr: T[]): T[] {
    const result = [...arr];
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  }

  // 旧互換メソッド
  getUnlockedCards(): CardDef[] {
    return ALL_CARDS;
  }
}
